import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Settings as SettingsIcon, 
  Key,
  Clock,
  MessageSquare,
  Save,
  Mail,
  Send,
  Phone,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/toast";
import { getSettings, updateSettings } from "@/lib/marketing-api";

export default function Settings() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [settings, setSettings] = useState({
    telegram_bot_token: "",
    resend_api_key: "",
    sender_email: "",
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_phone_number: "",
    default_message_delay: 1,
    max_messages_per_hour: 100
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success(t('success.saved'));
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-foreground">{t('marketingFull.settingsTitle')}</h1>
        <p className="text-muted-foreground mt-1">{t('marketingFull.settingsSubtitle')}</p>
      </div>

      <form onSubmit={handleSave}>
        <Tabs defaultValue="telegram" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="telegram" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              {t('marketingFull.telegram')}
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('marketingFull.email')}
            </TabsTrigger>
            <TabsTrigger value="sms" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              SMS
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              {t('common.general')}
            </TabsTrigger>
          </TabsList>

          {/* Telegram Settings */}
          <TabsContent value="telegram">
            <Card data-testid="telegram-settings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-400" />
                  {t('marketingFull.telegramSettings')}
                </CardTitle>
                <CardDescription>
                  {t('marketingFull.telegramSettingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="telegram_bot_token">Telegram Bot Token</Label>
                  <Input
                    id="telegram_bot_token"
                    type="password"
                    value={settings.telegram_bot_token || ""}
                    onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
                    placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                    className="font-numbers"
                    data-testid="telegram-token-input"
                  />
                </div>

                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <h4 className="font-medium mb-2 text-blue-400">{t('marketingFull.getBotToken')}</h4>
                  <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                    <li>{isRTL ? 'افتح تطبيق تلجرام وابحث عن @BotFather' : 'Open Telegram and search for @BotFather'}</li>
                    <li>{isRTL ? 'أرسل الأمر /newbot لإنشاء بوت جديد' : 'Send /newbot command to create a new bot'}</li>
                    <li>{isRTL ? 'اتبع التعليمات لاختيار اسم ومعرف للبوت' : 'Follow instructions to choose name and username'}</li>
                    <li>{isRTL ? 'انسخ التوكن المُعطى والصقه هنا' : 'Copy the given token and paste it here'}</li>
                  </ol>
                </div>

                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <h4 className="font-medium mb-2 text-yellow-400">⚠️ {t('marketingFull.telegramLimits')}</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li><strong>{t('marketingFull.botLimits')}</strong></li>
                    <li className="text-green-400"><strong>✅ {t('marketingFull.bestSolution')}</strong></li>
                    <li><strong>{t('marketingFull.alternativeSolution')}</strong> {isRTL ? 'استخدم بوتات متعددة لتوزيع الحمل' : 'Use multiple bots to distribute the load'}</li>
                    <li><strong>{t('marketingFull.alternativeSolution')}</strong> {t('marketingFull.scheduleCampaignOverDays')}</li>
                    <li><strong>{t('marketingFull.alternativeSolution')}</strong> {t('marketingFull.telegramEmailSms')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <Card data-testid="email-settings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-green-400" />
                  {t('marketingFull.emailSettings')}
                </CardTitle>
                <CardDescription>
                  {t('marketingFull.emailSettingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="resend_api_key">Resend API Key</Label>
                  <Input
                    id="resend_api_key"
                    type="password"
                    value={settings.resend_api_key || ""}
                    onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                    placeholder="re_xxxxxxxxxxxx"
                    className="font-numbers"
                    data-testid="resend-api-key-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="sender_email">{t('marketingFull.senderEmail')}</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={settings.sender_email || ""}
                    onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                    data-testid="sender-email-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('marketingFull.senderEmailNote')}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <h4 className="font-medium mb-2 text-green-400">{t('marketingFull.emailNotes')}</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• {isRTL ? 'في وضع الاختبار، الرسائل تُرسل فقط للبريد المُسجل' : 'In test mode, emails are sent only to registered email'}</li>
                    <li>• {isRTL ? 'للإرسال لأي بريد، أضف نطاقك الخاص في Resend' : 'To send to any email, add your domain in Resend'}</li>
                    <li>• {isRTL ? 'تأكد من التحقق من النطاق قبل الإرسال' : 'Verify your domain before sending'}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Settings */}
          <TabsContent value="sms">
            <Card data-testid="sms-settings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-purple-400" />
                  {t('marketingFull.smsSettings')}
                </CardTitle>
                <CardDescription>
                  {t('marketingFull.smsSettingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="twilio_account_sid">Account SID</Label>
                  <Input
                    id="twilio_account_sid"
                    type="password"
                    value={settings.twilio_account_sid || ""}
                    onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="font-numbers"
                    data-testid="twilio-sid-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="twilio_auth_token">Auth Token</Label>
                  <Input
                    id="twilio_auth_token"
                    type="password"
                    value={settings.twilio_auth_token || ""}
                    onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="font-numbers"
                    data-testid="twilio-token-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="twilio_phone_number">{t('marketingFull.twilioPhoneNumber')}</Label>
                  <Input
                    id="twilio_phone_number"
                    value={settings.twilio_phone_number || ""}
                    onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                    placeholder="+1xxxxxxxxxx"
                    className="font-numbers"
                    data-testid="twilio-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('marketingFull.twilioPhoneNumberDesc')}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <h4 className="font-medium mb-2 text-purple-400">{t('marketingFull.getTwilioData')}</h4>
                  <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                    <li>{isRTL ? 'سجل في' : 'Sign up at'} <a href="https://twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">twilio.com</a></li>
                    <li>{isRTL ? 'من لوحة التحكم، انسخ Account SID و Auth Token' : 'From dashboard, copy Account SID and Auth Token'}</li>
                    <li>{isRTL ? 'اشترِ رقم هاتف أو استخدم رقم الاختبار' : 'Buy a phone number or use trial number'}</li>
                    <li>{isRTL ? 'الصق البيانات هنا' : 'Paste the data here'}</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Settings */}
          <TabsContent value="general">
            <Card data-testid="general-settings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  {t('marketingFull.generalSettings')}
                </CardTitle>
                <CardDescription>
                  {t('marketingFull.generalSettingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label htmlFor="default_message_delay">
                      <Clock className="w-4 h-4 inline me-1" />
                      {t('marketingFull.messageDelay')}
                    </Label>
                    <Input
                      id="default_message_delay"
                      type="number"
                      min="0"
                      value={settings.default_message_delay}
                      onChange={(e) => setSettings({ ...settings, default_message_delay: parseInt(e.target.value) || 0 })}
                      className="font-numbers"
                      data-testid="message-delay-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('marketingFull.messageDelayNote')}
                    </p>
                  </div>
                  <div className="form-group">
                    <Label htmlFor="max_messages_per_hour">
                      {t('marketingFull.maxMessagesPerHour')}
                    </Label>
                    <Input
                      id="max_messages_per_hour"
                      type="number"
                      min="1"
                      value={settings.max_messages_per_hour}
                      onChange={(e) => setSettings({ ...settings, max_messages_per_hour: parseInt(e.target.value) || 1 })}
                      className="font-numbers"
                      data-testid="max-messages-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('marketingFull.maxMessagesPerHourNote')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tips Card */}
        <Card className="border-primary/20 bg-primary/5 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              🚀 {t('marketingFull.millionCampaignStrategies')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h5 className="font-medium text-green-400">✅ {t('marketingFull.telegramChannelsBest')}</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('marketingFull.oneMessageReachesMillions')}</li>
                  <li>• {t('marketingFull.noSubscriberLimits')}</li>
                  <li>• {t('marketingFull.instantDelivery')}</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium text-blue-400">📧 {t('marketingFull.emailResend')}</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('marketingFull.freePlan')}</li>
                  <li>• {t('marketingFull.proPlan')}</li>
                  <li>• {t('marketingFull.enterprisePlan')}</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium text-purple-400">⏰ {t('marketingFull.timeDistribution')}</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('marketingFull.scheduleCampaignOverDays')}</li>
                  <li>• {t('marketingFull.systemManagesAutomatically')}</li>
                  <li>• {t('marketingFull.avoidBansAndLimits')}</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium text-orange-400">🔄 {t('marketingFull.multipleChannels')}</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('marketingFull.telegramEmailSms')}</li>
                  <li>• {t('marketingFull.widerAudienceReach')}</li>
                  <li>• {t('marketingFull.increasedResponseRates')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <Button 
            type="submit" 
            disabled={saving} 
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-8 py-3 text-lg shadow-lg shadow-emerald-500/30"
            size="lg"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 me-2 animate-spin" />
                {t('marketingFull.saving')}
              </>
            ) : (
              <>
                <Save className="w-5 h-5 me-2" />
                {t('marketingFull.saveSettings')}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
